import React, { FC, useState, useEffect, useMemo } from 'react'
import { ScrollView, TouchableOpacity, View, Text, Image } from 'react-native'
import { useNavigate } from 'react-router-dom'

import {
  AppBackground,
  Button,
  ResponsiveLogo,
} from '@ui/components'

import {
  Headline2,
  Headline5,
  BodyText,
  colors,
} from '@/aena/styles'

import { ReactComponent as BackIcon } from '@/assets/icons/back_icon.svg'
import { ReactComponent as AenaDogIcon } from '@/assets/icons/aena/aena_dog.svg'
import { ReactComponent as BoardingCheckIcon } from '@/assets/icons/rounded_check.svg'
import { isTravelValidated } from './boarding'
import { travelApi, TravelLogic, type ITravelData } from '@front/logic'
import { useAppSelector } from '@front/store'
import { getHomeInfo } from '@front/apis'
import { t } from 'i18next'
import { HeaderWithBackAena } from '@/components/headerwithback'

export const TravelList: FC = () => {
  const navigate = useNavigate()
  // Los viajes se muestran por mascota seleccionada. Al crear un viaje, add.tsx
  // selecciona esa mascota y navega aquí, de modo que el viaje recién creado se ve.
  const { guid: userGUID } = useAppSelector((state) => state.selectedPet)
  const ownerCard = useAppSelector((state) => state.user.ownerCardNumber)
  const [travels, setTravels] = useState<ITravelData[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [petAvatars, setPetAvatars] = useState<Record<string, string>>({})

  const loadPetAvatars = async () => {
    try {
      const home = await getHomeInfo({ lat: 0, lon: 0 })
      const map: Record<string, string> = {}
      home.data?.petList?.forEach((p: any) => { if (p?.guid) map[p.guid] = p.avatar })
      setPetAvatars(map)
    } catch (e) {
      console.debug('Error loading pet avatars:', e)
    }
  }

  useEffect(() => {
    if (!userGUID) {
      console.debug('No selected pet found, redirecting to home')
      navigate('/aena/home')
      return
    }
    loadTravels()
    loadPetAvatars()
  }, [userGUID])

  const loadTravels = async () => {
    if (!userGUID) {
      setError('No hay mascota seleccionada')
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)
      const response = await travelApi.getTravels({ userGUID })
      setTravels(response.data.travels)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar los viajes')
      console.error('Error loading travels:', err)
    } finally {
      setLoading(false)
    }
  }

  const TravelCard: React.FC<{ travel: ITravelData; onPress: () => void; onBoardingPress: () => void }> = ({ travel, onPress, onBoardingPress }) => {
    const route = TravelLogic.generateRouteText(travel.departureAirport, travel.arrivalAirport)
    // El viaje pasa a "validado" cuando su estado es confirmed/completed
    // (CONFIRMED/APPROVED/ACCEPTED en Neon). Solo entonces se ofrece el acceso
    // a la tarjeta Boarding Check-in.
    const validated = isTravelValidated(travel.status)

    return (
      <TouchableOpacity onPress={onPress} style={{
        backgroundColor: 'white',
        borderRadius: 12,
        padding: 16,
        marginBottom: 16,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 4,
        elevation: 3,
      }}>
        {/* Header con fecha y estado */}
        <View style={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 12
        }}>
          <View style={{
            backgroundColor: colors.aenaVerde,
            paddingHorizontal: 12,
            paddingVertical: 4,
            borderRadius: 16
          }}>
            <BodyText style={{ color: colors.blanco }}>
              Viaje a {travel.arrivalAirport.split(' ')[0]} {TravelLogic.formatShortDate(travel.flightDate)}
            </BodyText>
          </View>

          <View style={{
            backgroundColor: TravelLogic.getStatusColor(travel.status),
            paddingHorizontal: 8,
            paddingVertical: 4,
            borderRadius: 12
          }}>
            <BodyText style={{ color: colors.blanco }}>
              {TravelLogic.getStatusText(travel.status)}
            </BodyText>
          </View>
        </View>

        {/* Información del vuelo */}
        <View style={{ marginBottom: 12 }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 4 }}>
            <BodyText style={{ fontWeight: '600' }}>
              {travel.airline} ({travel.flightNumber})
            </BodyText>
          </View>

          <BodyText>
            ({travel.pets.length}) Mascota{travel.pets.length !== 1 ? 's' : ''} ({travel.pets.length})
          </BodyText>

          <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 4 }}>
            <BodyText>
              {route}
            </BodyText>
            <View style={{
              backgroundColor: colors.grisClaro,
              paddingHorizontal: 8,
              paddingVertical: 2,
              borderRadius: 8,
              marginLeft: 'auto'
            }}>
            </View>
          </View>
        </View>

        {/* Mascotas */}
        <View style={{
          flexDirection: 'row',
          alignItems: 'center',
          paddingTop: 8,
          borderTopWidth: 1,
          borderTopColor: colors.grisClaro
        }}>
          {travel.pets.map((pet: any, index: number) => {
            const avatar = petAvatars[pet.guid]
            return (
              <View key={pet.guid || index} style={{
                flexDirection: 'row',
                alignItems: 'center',
                marginRight: 16
              }}>
                {avatar ? (
                  <Image
                    source={{ uri: avatar }}
                    style={{ width: 24, height: 24, borderRadius: 12, marginRight: 6 }}
                  />
                ) : (
                  <View style={{ marginRight: 4 }}>
                    <AenaDogIcon width={20} height={20} />
                  </View>
                )}
                <BodyText style={{ fontSize: 14, color: colors.grisOscuro }}>
                  {pet.name}
                </BodyText>
              </View>
            )
          })}

          {/* Acceso a Boarding Check-in: solo cuando el viaje esta validado */}
          {validated && (
            <TouchableOpacity
              onPress={onBoardingPress}
              accessibilityLabel="Ver tarjeta de embarque"
              style={{
                marginLeft: 'auto',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 6,
                backgroundColor: colors.aenaVerde,
                paddingHorizontal: 10,
                paddingVertical: 6,
                borderRadius: 16,
              }}
            >
              <BoardingCheckIcon width={18} height={18} />
              <BodyText style={{ fontSize: 12, color: colors.blanco, paddingLeft: 0 }}>
                Check-in
              </BodyText>
            </TouchableOpacity>
          )}
        </View>
      </TouchableOpacity>
    )
  }


  const handleTravelPress = (travelId: string) => {
    navigate(`detail/${travelId}`)
  }

  const handleBoardingPress = (travelId: string) => {
    navigate(`${travelId}/boarding`)
  }

  const handleNewTravel = () => {
    navigate('add')
  }

  if (loading) {
    return (
      <AppBackground isSafe>
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
          <BodyText>Cargando viajes...</BodyText>
        </View>
      </AppBackground>
    )
  }

  if (error) {
    return (
      <AppBackground isSafe>
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: 20 }}>
          <BodyText style={{ color: colors.rojoInactivo }}>
            {error}
          </BodyText>
          <Button
            title="Reintentar"
            onPress={loadTravels}
            size="medium"
            textType="cta_medium"
            color="primary"
          />
        </View>
      </AppBackground>
    )
  }

  return (
    <AppBackground isSafe>
      <View style={{ maxWidth: 800, width: '100%', alignSelf: 'center' }}>
        <View style={{ flexDirection: 'row', justifyContent: 'center', alignItems: 'center', padding: 16, gap: 16 }}>
          <ResponsiveLogo
            breakpoint={820}
            hysteresis={20}
            large={{ width: 680, height: 96 }}
            small={{ width: 360, height: 48 }}
          />
        </View>

        <HeaderWithBackAena title="Mis viajes" navigateTo="/aena/home" />

        <ScrollView style={{ padding: 16 }}>
          {travels.length === 0 ? (
            <View style={{ paddingVertical: 60 }}>
              {/* Textos centrados, pero el boton a ancho completo (alignItems:center
                  impedia que width:'100%' se estirara -> salia pequeno). */}
              <View style={{ alignItems: 'center', marginBottom: 24 }}>
                <Headline5>
                  No tienes viajes registrados
                </Headline5>
                <BodyText>
                  Crea tu primer viaje para empezar
                </BodyText>
              </View>
              <Button
                title="Nuevo viaje"
                size="block"
                textType="cta_large"
                borderRadius={8}
                buttonWrapperStyle={{ backgroundColor: colors.aenaVerde, paddingVertical: 18, width: '100%' }}
                color="primary"
                onPress={handleNewTravel}
              />
            </View>
          ) : (
            <>
              <View style={{ marginBottom: 12 }}>
                <Headline2>
                  Tus Viajes
                </Headline2>
                <BodyText>
                  {travels.length} viaje{travels.length !== 1 ? 's' : ''} registrado{travels.length !== 1 ? 's' : ''}
                </BodyText>
              </View>

              <View style={{ marginBottom: 16 }}>
                <Button
                  title="Nuevo viaje"
                  size="block"
                  textType="cta_large"
                  borderRadius={8}
                  buttonWrapperStyle={{ backgroundColor: colors.aenaVerde, paddingVertical: 18, width: '100%' }}
                  color="primary"
                  onPress={handleNewTravel}
                />
              </View>

              {travels.map(travel => (
                <TravelCard
                  key={travel.id}
                  travel={travel}
                  onPress={() => handleTravelPress(travel.id)}
                  onBoardingPress={() => handleBoardingPress(travel.id)}
                />
              ))}
            </>
          )}
        </ScrollView>
      </View>
    </AppBackground>
  )
}

export default TravelList
